'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/lib/i18n/LanguageContext';
import { NETWORK_SITES, networkHref } from '@/lib/ads/network';

// Site-wide footer (2026-09-08, SEO pass). Before this the site had NO footer at all: the legal
// pages were unreachable, there was no internal-linking surface for crawlers, and the corporate
// network (FXDoctor, DayProp, Swiss Prime Markets, Predvidi.me, nikolabroceta.com) was invisible.
//
// Shown only on public / content routes. Hidden on app-like surfaces (feed for logged-in users,
// messages, live rooms, settings…) so it never gets in the way of the product.
const HIDDEN_PREFIXES = [
  '/messages', '/inbox', '/notifications', '/settings', '/admin', '/auth', '/live', '/chat',
  '/portfolio', '/watchlist', '/image-library', '/verify-account', '/sign-up-login-screen',
  '/invite', '/forgot-password', '/password-reset', '/token-activation',
];

type L = { sr: string; en: string };
const pick = (lang: 'sr' | 'en', l: L) => (lang === 'sr' ? l.sr : l.en);

const PRODUCT_LINKS: { href: string; label: L }[] = [
  { href: '/forum', label: { sr: 'Forum za trejdere', en: 'Trader forum' } },
  { href: '/discover', label: { sr: 'Signali i analize', en: 'Signals & analysis' } },
  { href: '/studije', label: { sr: 'Studije trejdova', en: 'Trade case studies' } },
  { href: '/signali', label: { sr: 'Signali po instrumentu', en: 'Signals by instrument' } },
  { href: '/leaderboard', label: { sr: 'Rang lista trejdera', en: 'Trader leaderboard' } },
  { href: '/channels', label: { sr: 'Premium kanali', en: 'Premium channels' } },
  { href: '/battles', label: { sr: 'Bitke trejdera', en: 'Trader battles' } },
  { href: '/vesti', label: { sr: 'Vesti sa tržišta', en: 'Market news' } },
];

const ACCOUNT_LINKS: { href: string; label: L }[] = [
  { href: '/sign-up-login-screen', label: { sr: 'Besplatna registracija', en: 'Free sign-up' } },
  { href: '/premium', label: { sr: 'Otvori premium kanal', en: 'Open a premium channel' } },
  { href: '/referral', label: { sr: 'Preporuči i zaradi', en: 'Refer & earn' } },
  { href: '/terms', label: { sr: 'Uslovi korišćenja', en: 'Terms of use' } },
  { href: '/privacy', label: { sr: 'Privatnost', en: 'Privacy' } },
];

export default function SiteFooter() {
  const pathname = usePathname() || '';
  const { user } = useAuth();
  const { lang } = useLang();

  if (HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  // "/" is the feed for logged-in users (infinite scroll) — no footer there.
  if (user && (pathname === '/' || pathname.startsWith('/home-feed'))) return null;

  const year = new Date().getFullYear();
  const linkCls = 'block text-[13.5px] leading-[1.9] hover:underline';
  const headCls = 'text-[11px] font-extrabold uppercase tracking-[0.12em] mb-2';

  return (
    <footer
      className="mt-16"
      style={{
        borderTop: '1px solid var(--border)',
        background: 'var(--bg-card)',
        color: 'var(--text-secondary)',
        // Leave room for the floating mobile bottom nav when it is visible.
        paddingBottom: user ? 'calc(110px + env(safe-area-inset-bottom))' : undefined,
      }}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 pt-12 pb-8">
        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
          {/* Brand + what we are (crawlable summary of the product) */}
          <div>
            <Link href="/" className="font-heading font-extrabold text-[20px]" style={{ color: 'var(--text-primary)' }}>
              ShowMe<span style={{ color: 'var(--accent)' }}>Trade</span>
            </Link>
            <p className="mt-3 text-[14px] leading-[1.6] max-w-[380px]">
              {pick(lang, {
                sr: 'Zajednica trejdera sa Balkana: signali sa ulazom, stopom i ciljem na simuliranom nalogu, javni skor 0–100 koji zatvara tržište, forum na srpskom i rang lista trejdera. Forex, zlato, kripto, akcije — besplatno.',
                en: 'A Balkan trading community: signals with entry, stop and target on a simulated account, a public 0–100 score closed by the market, a forum and a trader leaderboard. Forex, gold, crypto, stocks — free.',
              })}
            </p>
            <a
              href="https://www.instagram.com/showmetrade"
              target="_blank"
              rel="noopener"
              className="inline-block mt-4 text-[13.5px] font-semibold hover:underline"
              style={{ color: 'var(--accent)' }}
            >
              Instagram @showmetrade ↗
            </a>
          </div>

          <div>
            <div className={headCls} style={{ color: 'var(--text-muted)' }}>{pick(lang, { sr: 'Platforma', en: 'Platform' })}</div>
            {PRODUCT_LINKS.map((l) => (
              <Link key={l.href} href={l.href} className={linkCls}>{pick(lang, l.label)}</Link>
            ))}
          </div>

          <div>
            <div className={headCls} style={{ color: 'var(--text-muted)' }}>{pick(lang, { sr: 'Nalog', en: 'Account' })}</div>
            {ACCOUNT_LINKS.map((l) => (
              <Link key={l.href} href={l.href} className={linkCls}>{pick(lang, l.label)}</Link>
            ))}
          </div>

          {/* Corporate network — editorial cross-links between the group's own sites */}
          <div>
            <div className={headCls} style={{ color: 'var(--text-muted)' }}>{pick(lang, { sr: 'Naši projekti', en: 'Our network' })}</div>
            {NETWORK_SITES.map((s) => (
              <a
                key={s.host}
                href={networkHref(s, 'footer')}
                target="_blank"
                rel="noopener"
                className={linkCls}
                title={pick(lang, s.tagline)}
              >
                <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{s.name}</span>
                <span className="hidden sm:inline"> — {pick(lang, s.tagline)}</span>
              </a>
            ))}
          </div>
        </div>

        <div
          className="mt-10 pt-5 flex flex-col sm:flex-row gap-2 sm:gap-6 justify-between text-[12.5px] leading-[1.6]"
          style={{ borderTop: '1px solid var(--border)', color: 'var(--text-muted)' }}
        >
          <div>© {year} ShowMeTrade · Balkan Bug LLC</div>
          <div className="max-w-[760px]">
            {pick(lang, {
              sr: 'Trgovanje nosi rizik. Svi signali na ShowMeTrade su simulirani i edukativni — nisu investicioni savet. Ne trgujte novcem koji ne možete da izgubite.',
              en: 'Trading carries risk. All signals on ShowMeTrade are simulated and educational — not investment advice. Never trade money you cannot afford to lose.',
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}
